import React, { useState, useEffect, useRef } from "react";
import "./MissionBylaws.scss";

const MissionBylaws = () => {
    // Refs for scroll animations
    const missionRef = useRef(null);
    const bylawsRef = useRef(null);
    const historyRef = useRef(null);
    const valuesRef = useRef(null);
    
    // Animation states
    const [missionVisible, setMissionVisible] = useState(false);
    const [bylawsVisible, setBylawsVisible] = useState(false);
    const [historyVisible, setHistoryVisible] = useState(false);
    const [valuesVisible, setValuesVisible] = useState(false);

    // Intersection Observer for scroll animations
    useEffect(() => {
        const observerOptions = {
            threshold: 0.2,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === missionRef.current && entry.isIntersecting) {
                    setMissionVisible(true);
                }
                if (entry.target === bylawsRef.current && entry.isIntersecting) {
                    setBylawsVisible(true);
                }
                if (entry.target === historyRef.current && entry.isIntersecting) {
                    setHistoryVisible(true);
                }
                if (entry.target === valuesRef.current && entry.isIntersecting) {
                    setValuesVisible(true);
                }
            });
        }, observerOptions);

        if (missionRef.current) observer.observe(missionRef.current);
        if (bylawsRef.current) observer.observe(bylawsRef.current);
        if (historyRef.current) observer.observe(historyRef.current);
        if (valuesRef.current) observer.observe(valuesRef.current);

        return () => {
            if (missionRef.current) observer.unobserve(missionRef.current);
            if (bylawsRef.current) observer.unobserve(bylawsRef.current);
            if (historyRef.current) observer.unobserve(historyRef.current);
            if (valuesRef.current) observer.unobserve(valuesRef.current);
        };
    }, []);

    return (
        <div className="mission-bylaws">
            {/* Hero Section */}
            <div className="mission-bylaws__hero">
                <div className="hero-content">
                    <div className="hero-text">
                        <h1>Mission Statement & Bylaws</h1>
                        <p>Guided by faith, united in purpose, committed to service</p>
                    </div>
                </div>
                <div className="hero-decoration">
                    <div className="decoration-cross"></div>
                </div>
            </div>

            <div className="mission-bylaws__container">
                <div className="mission-bylaws__content">
                    {/* Mission Statement Section */}
                    <section ref={missionRef} className={`mission-section ${missionVisible ? 'animate-in' : ''}`}>
                        <div className="section-header">
                            <div className="section-number">01</div>
                            <h2>Mission Statement</h2>
                            <div className="section-line"></div>
                        </div>
                        
                        <div className="mission-content">
                            <div className={`mission-quote ${missionVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.2s' }}>
                                <blockquote>
                                    "The mission of the Ladies' Guild is to unite the women of our Church 
                                    to support and assist the Pastor and Board of Trustees in various 
                                    endeavors and activities relating to our parish."
                                </blockquote>
                            </div>
                            
                            <div className="mission-details">
                                <div className={`detail-item ${missionVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.4s' }}>
                                    <h4>Community Engagement</h4>
                                    <p>
                                        Our involvement extends to the Sunday School, Nareg Armenian School, 
                                        the Choir, and Church youth-oriented organizations as required.
                                    </p>
                                </div>
                                
                                <div className={`detail-item ${missionVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.6s' }}>
                                    <h4>Cultural Preservation</h4>
                                    <p>
                                        We inspire our members and local parishioners to learn the doctrines 
                                        and traditions of the Armenian Apostolic Church, sharing our heritage 
                                        with members of other denominations in our community.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Bylaws Section */}
                    <section ref={bylawsRef} className={`bylaws-section ${bylawsVisible ? 'animate-in' : ''}`}>
                        <div className="section-header">
                            <div className="section-number">02</div>
                            <h2>Organizational Bylaws</h2>
                            <div className="section-line"></div>
                        </div>
                        
                        <div className="bylaws-content">
                            <div className={`bylaws-notice ${bylawsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.2s' }}>
                                <p>
                                    Our comprehensive bylaws document is currently being prepared for publication. 
                                    These guidelines will outline our organizational structure, procedures, and governance principles.
                                </p>
                            </div>
                            
                            <div className={`bylaws-preview ${bylawsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.4s' }}>
                                <h4>Upcoming Documentation</h4>
                                <div className="preview-grid">
                                    <div className={`preview-item ${bylawsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.6s' }}>
                                        <span className="item-number">I</span>
                                        <span>Organizational Structure</span>
                                    </div>
                                    <div className={`preview-item ${bylawsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.7s' }}>
                                        <span className="item-number">II</span>
                                        <span>Membership Requirements</span>
                                    </div>
                                    <div className={`preview-item ${bylawsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.8s' }}>
                                        <span className="item-number">III</span>
                                        <span>Meeting Procedures</span>
                                    </div>
                                    <div className={`preview-item ${bylawsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.9s' }}>
                                        <span className="item-number">IV</span>
                                        <span>Officer Responsibilities</span>
                                    </div>
                                    <div className={`preview-item ${bylawsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '1.0s' }}>
                                        <span className="item-number">V</span>
                                        <span>Financial Guidelines</span>
                                    </div>
                                    <div className={`preview-item ${bylawsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '1.1s' }}>
                                        <span className="item-number">VI</span>
                                        <span>Event Planning Protocols</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* History Section */}
                    <section ref={historyRef} className={`history-section ${historyVisible ? 'animate-in' : ''}`}>
                        <div className="section-header">
                            <div className="section-number">03</div>
                            <h2>Then and Now</h2>
                            <div className="section-line"></div>
                        </div>
                        
                        <div className="history-content">
                            <div className={`history-intro ${historyVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.2s' }}>
                                <p className="history-intro-text">
                                    Our Church was consecrated on May 3, 1959. However, several committees and organizations 
                                    were formed years prior in order to lay a foundation and provide much needed financial 
                                    support for this major endeavor. One such organization was the Ladies' Auxiliary of the 
                                    Armenian Apostolic Church of New Jersey, now called the Ladies' Guild of Sts. Vartanantz Church.
                                </p>
                                <p className="history-intro-text">
                                    Throughout the years, our Church has been nurtured and supported by our strong and dedicated women.
                                </p>
                            </div>

                            
                            <div className={`history-timeline ${historyVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.4s' }}>
                                <div className="timeline-section">
                                    <div className="timeline-header">
                                        <h3>THEN</h3>
                                        <span className="timeline-subtitle">History from the Consecration Album of Sts. Vartanantz</span>
                                    </div>                                    <div className="timeline-content">
                                        <div className="timeline-quote">
                                            <blockquote>
                                                Forever dedicated to the preservation of her heritage, the Armenian woman once again 
                                                organized all her untiring efforts to help bring about the construction of an edifice which would 
                                                embrace and perpetuate her glorious Apostolic religion, her culture and her spirit.
                                            </blockquote>
                                        </div>

                                        <div className="timeline-events">
                                            <div className="timeline-event">
                                                <div className="event-date">May 8, 1954</div>
                                                <div className="event-content" data-date="May 8, 1954">
                                                    <h4>Foundation</h4>
                                                    <p>
                                                        The Ladies Auxiliary of the Armenian Apostolic Church of New Jersey was officially 
                                                        organized by a nucleus of thirty-four women at a Saturday evening tea sponsored by 
                                                        the Board of Trustees, at Armenia Hall in Union City, NJ. The women mainly represented 
                                                        Hudson, Bergen, Passaic, and Essex counties.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="timeline-event">
                                                <div className="event-date">1954</div>
                                                <div className="event-content" data-date="1954">
                                                    <h4>First Active Year</h4>
                                                    <p>
                                                        The first active year was nurtured with unending determination, drive, and enthusiasm. 
                                                        Financial gains netting $5,000.00 was brought about by sponsoring a Bazaar held in 
                                                        October 1954, and a dinner and raffle. By the end of 1954, membership had increased to over 75 active members.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="timeline-event">
                                                <div className="event-date">1954-1959</div>
                                                <div className="event-content" data-date="1954-1959">
                                                    <h4>Growth and Dedication</h4>
                                                    <p>
                                                        Over the next several years membership flourished to over 100 members by the time of the 
                                                        Church's consecration. The Bazaar became an annual event as did raffles, dinners, card parties, 
                                                        and rummage sales. All told, the Ladies' Auxiliary raised and donated $25,000.00 towards the building of the Church.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="timeline-event">
                                                <div className="event-date">May 1959</div>
                                                <div className="event-content" data-date="May 1959">
                                                    <h4>Church Consecration</h4>
                                                    <p>
                                                        Once the Church was consecrated and the halls were opened in May 1959, the organization 
                                                        was the first to host several events that same year, most notably a Community Dinner to honor 
                                                        the new priest Der Kourken Yaralian and his family and the Annual Bazaar.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="timeline-section">
                                    <div className="timeline-header">
                                        <h3>NOW</h3>
                                        <span className="timeline-subtitle">Continuing Our Legacy</span>
                                    </div>
                                    
                                    <div className="timeline-content">
                                        <div className="now-content">
                                            <p>
                                                As we fast forward through the years, many things have changed, yet many remain the same. 
                                                Our members still have the same desire to support our Church and see it grow and thrive. 
                                                We have learned to adapt in order to stay relevant to our members while contributing to the Church.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Core Values Section */}
                    <section ref={valuesRef} className={`values-section ${valuesVisible ? 'animate-in' : ''}`}>
                        <div className="section-header">
                            <div className="section-number">04</div>
                            <h2>Core Values</h2>
                            <div className="section-line"></div>
                        </div>
                        
                        <div className="values-content">
                            <div className="values-grid">
                                <div className={`value-card ${valuesVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.2s' }}>
                                    <div className="value-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                                            <path d="M2 17l10 5 10-5"/>
                                            <path d="M2 12l10 5 10-5"/>
                                        </svg>
                                    </div>
                                    <h4>Faith</h4>
                                    <p>Deepening our connection to Armenian Apostolic traditions and strengthening our spiritual foundation</p>
                                </div>
                                
                                <div className={`value-card ${valuesVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.4s' }}>
                                    <div className="value-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                            <circle cx="9" cy="7" r="4"/>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                        </svg>
                                    </div>
                                    <h4>Community</h4>
                                    <p>Building lasting bonds among the women of our parish and fostering inclusive fellowship</p>
                                </div>
                                
                                <div className={`value-card ${valuesVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.6s' }}>
                                    <div className="value-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                                        </svg>
                                    </div>
                                    <h4>Service</h4>
                                    <p>Supporting our church leadership and extending compassionate outreach to our broader community</p>
                                </div>
                                
                                {/* <div className={`value-card ${valuesVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.8s' }}>
                                    <div className="value-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                                            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                                        </svg>
                                    </div>
                                    <h4>Education</h4>
                                    <p>Sharing Armenian culture, church teachings, and preserving our rich heritage for future generations</p>
                                </div> */}
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default MissionBylaws;
